import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useModuleAccess } from '../../hooks/useModuleAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  module?: string;
  requireAll?: boolean;
  redirectTo?: string;
  fallback?: React.ReactNode;
}

/**
 * ProtectedRoute component that guards routes based on permissions or module access
 */
const ProtectedRoute = ({
  children,
  permission,
  permissions,
  module,
  requireAll = false,
  redirectTo = '/',
  fallback = null,
}: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  const { canAccessModule } = useModuleAccess();
  useLocation();

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Demo mode: grant full access to anonymous/assessment_only users
  if (user?.is_anonymous || user?.subscription_plan === 'assessment_only' || user?.client?.subscription_plan === 'assessment_only') {
    return <>{children}</>;
  }

  // Check permissions or module access
  let hasAccess = true;

  if (module && user?.role === 'aws' && !user?.is_admin) {
    // AWS users use module access instead of permission checks
    hasAccess = canAccessModule(module);
  } else if (permission) {
    // Single permission check
    hasAccess = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    // Multiple permissions check
    hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  }

  // If access denied, show fallback or redirect
  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
